/**
 * Canva response NORMALIZER — pure, no API/runtime dependency so it can be
 * unit-tested on its own. Canva list payloads arrive in several shapes
 * depending on which backend route answered; every one of them collapses to
 * { id, name, breadcrumb, folders, items, source } for the navigator.
 */
import { FOLDER_NAMES } from './canvaFolders.js'
import { isFolderEntry, entryId } from '../studio/canvaEntry.js'

/** Canva list responses come in several shapes. Normalize them all to one. */
export function normalizeCanvaNode(res, folderId) {
  if (!res || typeof res !== 'object') return null

  // Already in navigator shape.
  if (Array.isArray(res.folders) || Array.isArray(res.items)) {
    const folders = (res.folders || []).map(normalizeFolder).filter(Boolean)
    const items = (res.items || []).map(normalizeItem).filter(Boolean)
    // A flat `items` array can legitimately carry folder entries too.
    const nested = items.filter((i) => i.isFolder)
    return {
      id: res.id || folderId,
      name: res.name || FOLDER_NAMES[folderId] || '',
      breadcrumb: Array.isArray(res.breadcrumb) ? res.breadcrumb : null,
      folders: folders.concat(nested.map((f) => ({ id: f.id, name: f.name }))),
      items: items.filter((i) => !i.isFolder),
      // Canva pages its folder listings. Dropping the token here is what made a
      // 300-design folder read as one page — the navigator can only follow a
      // cursor it is given.
      continuation: res.continuation || null,
      source: 'canva',
    }
  }

  // Canva REST list shape: { items: [{ type: 'folder'|'design', folder|design }] }
  const raw = res.results || res.data || res.contents || null
  if (Array.isArray(raw)) {
    const mapped = raw.map(normalizeItem).filter(Boolean)
    return {
      id: folderId,
      name: FOLDER_NAMES[folderId] || '',
      breadcrumb: null,
      folders: mapped.filter((i) => i.isFolder).map((f) => ({ id: f.id, name: f.name })),
      items: mapped.filter((i) => !i.isFolder),
      continuation: res.continuation || null,
      source: 'canva',
    }
  }
  return null
}

function normalizeFolder(f) {
  if (!f) return null
  const node = f.folder || f
  const id = entryId(f)
  if (!id) return null
  return { id, name: node.name || node.title || 'Folder' }
}

/** One Canva entry → { id, name, thumb, url, isFolder }. */
function normalizeItem(entry) {
  if (!entry) return null
  const node = entry.design || entry.folder || entry.image || entry.asset || entry
  const id = entryId(entry)
  if (!id) return null
  // ONE shared classifier (studio/canvaEntry.js). This line used to re-derive
  // the rule from `entry.type` alone, which the live route never sends — every
  // folder read as a design, so a sub-folder rendered as a tile that SELECTS
  // instead of OPENING and drill-down was unreachable. See canvaEntry.js.
  const isFolder = isFolderEntry(entry)
  const thumb =
    // `thumb` FIRST: it is what the live route actually sends. `canva.js#mapDesign`
    // flattens Canva's `design.thumbnail.url` down to `thumb`, so a chain that
    // only knew the nested spellings resolved to '' and every real design tile
    // rendered with no preview at all.
    node.thumb ||
    node.thumbnail?.url ||
    node.thumbnail_url ||
    node.thumbnailUrl ||
    node.preview?.url ||
    node.previewUrl ||
    node.urls?.thumbnail ||
    ''
  const url =
    node.urls?.png ||
    node.urls?.view_url ||
    node.export?.url ||
    node.url ||
    node.view || // the live route's flattened view_url
    thumb
  return {
    id: String(id),
    name: node.title || node.name || 'Untitled',
    thumb,
    url,
    // Canva's own edit/view link, when the payload carries one — lets the
    // Elements tab open a design in Canva rather than just previewing it.
    edit: node.urls?.edit_url || node.edit_url || node.editUrl || node.urls?.view_url || node.view || '',
    isFolder,
  }
}
